import { useCallback, useEffect, useState } from 'react';
import useAuth from './useAuth';
import { getOccupationFieldOptions } from '../services/userService';

const DEFAULT_LIMIT = 200;

const cache = {
  data: null,
  promise: null,
};

const normalizeOptions = (raw) => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }
      const { id, codigo, descripcion } = item;
      if (typeof id !== 'string' || id.length === 0) {
        return null;
      }
      const trimmedCode =
        typeof codigo === 'string' && codigo.trim().length > 0
          ? codigo.trim()
          : null;
      const trimmedDescription =
        typeof descripcion === 'string' && descripcion.trim().length > 0
          ? descripcion.trim()
          : null;
      const labelParts = [];
      if (trimmedCode) {
        labelParts.push(trimmedCode);
      }
      if (trimmedDescription) {
        labelParts.push(trimmedDescription);
      }
      const label =
        labelParts.length > 0 ? labelParts.join(' - ') : id;
      return { value: id, label };
    })
    .filter(Boolean);
};

const fetchWithCache = async (authFetch) => {
  if (!authFetch) {
    return [];
  }

  if (cache.data) {
    return cache.data;
  }

  if (!cache.promise) {
    cache.promise = getOccupationFieldOptions(authFetch, { limit: DEFAULT_LIMIT })
      .then((data) => {
        const normalized = normalizeOptions(data);
        cache.data = normalized;
        return normalized;
      })
      .catch((error) => {
        cache.data = null;
        throw error;
      })
      .finally(() => {
        cache.promise = null;
      });
  }

  return cache.promise;
};

const useOccupationFieldsCatalog = () => {
  const { authFetch } = useAuth();
  const [options, setOptions] = useState(() => []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!authFetch) {
      setOptions([]);
      setLoading(false);
      setError(null);
      cache.data = null;
      return [];
    }

    setLoading(true);
    setError(null);
    try {
      const data = await getOccupationFieldOptions(authFetch, {
        limit: DEFAULT_LIMIT,
      });
      const normalized = normalizeOptions(data);
      cache.data = normalized;
      setOptions(normalized);
      setLoading(false);
      return normalized;
    } catch (err) {
      const normalizedError =
        err instanceof Error
          ? err
          : new Error('No fue posible cargar el catálogo de campos de ocupación.');
      cache.data = null;
      setOptions([]);
      setLoading(false);
      setError(normalizedError);
      throw normalizedError;
    }
  }, [authFetch]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!authFetch) {
        if (active) {
          setOptions([]);
          setLoading(false);
          setError(null);
        }
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const data = await fetchWithCache(authFetch);
        if (!active) {
          return;
        }
        setOptions(data);
        setLoading(false);
      } catch (err) {
        if (!active) {
          return;
        }
        const normalizedError =
          err instanceof Error
            ? err
            : new Error('No fue posible cargar el catálogo de campos de ocupación.');
        setOptions([]);
        setLoading(false);
        setError(normalizedError);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [authFetch]);

  return {
    options,
    loading,
    error,
    refresh,
  };
};

export default useOccupationFieldsCatalog;
